import { useState, useRef, useEffect, useMemo, type PointerEvent as ReactPointerEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, GripVertical, RotateCcw, Plus, MessageSquare, Pin, Pencil, Trash2, ChevronDown, ChevronLeft, ChevronRight, Check, Sparkles, Move } from 'lucide-react';
import { AIContextService } from '../../services/ai/AIContextService';
import { ProactiveMonitoringService, type MonitoringEvent } from '../../services/ai/ProactiveMonitoringService';
import { ComoRobotIcon } from './ComoRobotIcon';
import { useResponsive } from '../../hooks/useResponsive';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface Message {
  id: string;
  type: 'user' | 'como' | 'alert';
  content: string;
  suggestions?: string[];
  severity?: 'info' | 'warning' | 'critical';
  timestamp: Date;
}

interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  pinned?: boolean;
  createdAt: string;
  updatedAt: string;
}

type ComoMood = 'idle' | 'thinking' | 'happy' | 'concerned' | 'excited' | 'alert';
type Position = { x: number; y: number };
type Size = { width: number; height: number };
type ResizeDirection =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/** Bidirectional resize cursors (ns = vertical, ew = horizontal, diagonals for corners). */
const RESIZE_CURSOR: Record<ResizeDirection, string> = {
  top: 'ns-resize',
  bottom: 'ns-resize',
  left: 'ew-resize',
  right: 'ew-resize',
  'top-left': 'nwse-resize',
  'top-right': 'nesw-resize',
  'bottom-left': 'nesw-resize',
  'bottom-right': 'nwse-resize',
};

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('coremonit_user') || '{}');
  } catch {
    return {};
  }
};

const getChatStorageKey = () => {
  const user = getCurrentUser();
  const userId = user.id || user.email || 'guest';
  return `como_chat_sessions_${userId}`;
};

const getFabPositionStorageKey = () => {
  const user = getCurrentUser();
  const userId = user.id || user.email || 'guest';
  return `como_fab_position_${userId}`;
};

const getContrastStorageKey = () => {
  const user = getCurrentUser();
  const userId = user.id || user.email || 'guest';
  return `como_high_contrast_${userId}`;
};

const getClarityStorageKey = () => {
  const user = getCurrentUser();
  const userId = user.id || user.email || 'guest';
  return `como_chat_clarity_${userId}`;
};

const getGreetingSeenSessionKey = () => {
  const user = getCurrentUser();
  const userId = user.id || user.email || 'guest';
  return `como_signin_greeting_seen_${userId}`;
};

const createWelcomeMessage = (): Message => {
  const user = getCurrentUser();
  const firstName = user.name?.split(' ')[0];
  return {
    id: `welcome-${Date.now()}`,
    type: 'como',
    content: firstName
      ? `Hey ${firstName}! I'm COMO, your CoreMonit AI assistant. I can help with infrastructure monitoring, troubleshooting, and general questions.`
      : 'Hey! I\'m COMO, your CoreMonit AI assistant. I can help with infrastructure monitoring, troubleshooting, and general questions.',
    timestamp: new Date(),
  };
};

const parseStoredMessages = (raw: string): Message[] => {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((msg) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }))
      .filter((msg) => msg?.id && msg?.content && msg?.timestamp);
  } catch {
    return [];
  }
};

const parseStoredSessions = (raw: string): ChatSession[] => {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((session) => ({
        ...session,
        pinned: !!session.pinned,
        messages: Array.isArray(session.messages)
          ? session.messages.map((msg: Message) => ({ ...msg, timestamp: new Date(msg.timestamp) }))
          : [],
      }))
      .filter((session) => session?.id && session?.title);
  } catch {
    return [];
  }
};

export function ComoAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [mood, setMood] = useState<ComoMood>('idle');
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isWinking, setIsWinking] = useState(false);
  const [isDroppingFab, setIsDroppingFab] = useState(false);
  const [showSignInGreeting, setShowSignInGreeting] = useState(false);
  const [isFirstTimeGreeting, setIsFirstTimeGreeting] = useState(false);
  const [greetingMessage, setGreetingMessage] = useState('');
  const [hasUnreadAlert, setHasUnreadAlert] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [openChatIds, setOpenChatIds] = useState<string[]>([]);
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteConfirmChatId, setDeleteConfirmChatId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatStorageKey, setChatStorageKey] = useState(getChatStorageKey());
  const [fabPositionStorageKey, setFabPositionStorageKey] = useState(getFabPositionStorageKey());
  const [contrastStorageKey, setContrastStorageKey] = useState(getContrastStorageKey());
  const [clarityStorageKey, setClarityStorageKey] = useState(getClarityStorageKey());
  const [highContrast, setHighContrast] = useState(false);
  const [chatClarity, setChatClarity] = useState(74);
  const [isChatsMenuOpen, setIsChatsMenuOpen] = useState(false);
  const [canScrollHeaderLeft, setCanScrollHeaderLeft] = useState(false);
  const [canScrollHeaderRight, setCanScrollHeaderRight] = useState(false);
  const [fabPosition, setFabPosition] = useState<Position | null>(null);
  const [dragGhostPosition, setDragGhostPosition] = useState<Position | null>(null);
  const [chatPosition, setChatPosition] = useState<Position | null>(null);
  const [chatSize, setChatSize] = useState<Size>({ width: 384, height: 560 });
  const [isChatDragging, setIsChatDragging] = useState(false);
  const [isChatResizing, setIsChatResizing] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatScrollPositionsRef = useRef<Record<string, number>>({});
  const dragOffsetRef = useRef<Position>({ x: 0, y: 0 });
  const movedDuringDragRef = useRef(false);
  const activeChatIdRef = useRef<string | null>(null);
  const chatDragOffsetRef = useRef<Position>({ x: 0, y: 0 });
  const headerActionsScrollRef = useRef<HTMLDivElement>(null);
  const chatResizeRef = useRef<{
    startX: number;
    startY: number;
    width: number;
    height: number;
    x: number;
    y: number;
    direction: ResizeDirection;
  } | null>(null);
  const monitoringService = useRef(ProactiveMonitoringService.getInstance());
  
  // Responsive hook
  const { isMobile, isTablet } = useResponsive();

  const activeSession = useMemo(
    () => chatSessions.find((session) => session.id === activeChatId) || chatSessions[0] || null,
    [chatSessions, activeChatId]
  );
  const sortedSessions = useMemo(
    () =>
      [...chatSessions].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }),
    [chatSessions]
  );
  const messages = activeSession?.messages || [];

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  useEffect(() => {
    if (!activeChatId) return;
    setOpenChatIds((prev) => (prev.includes(activeChatId) ? prev : [...prev, activeChatId]));
  }, [activeChatId]);

  const clampPosition = (x: number, y: number): Position => {
    const margin = 8;
    const fabSize = 64;
    const maxX = Math.max(margin, window.innerWidth - fabSize - margin);
    const maxY = Math.max(margin, window.innerHeight - fabSize - margin);

    return {
      x: Math.min(Math.max(x, margin), maxX),
      y: Math.min(Math.max(y, margin), maxY),
    };
  };

  const getDefaultFabPosition = (): Position => {
    return {
      x: window.innerWidth - 88,
      y: window.innerHeight - 88,
    };
  };

  const clampChatSize = (size: Size): Size => {
    const minWidth = 320;
    const minHeight = 420;
    const maxWidth = Math.max(minWidth, window.innerWidth - 16);
    const maxHeight = Math.max(minHeight, window.innerHeight - 16);
    return {
      width: Math.min(Math.max(size.width, minWidth), maxWidth),
      height: Math.min(Math.max(size.height, minHeight), maxHeight),
    };
  };

  const clampChatPosition = (position: Position, size: Size): Position => {
    const margin = 8;
    const maxX = Math.max(margin, window.innerWidth - size.width - margin);
    const maxY = Math.max(margin, window.innerHeight - size.height - margin);
    return {
      x: Math.min(Math.max(position.x, margin), maxX),
      y: Math.min(Math.max(position.y, margin), maxY),
    };
  };

  const createChatSession = (title?: string): ChatSession => {
    const now = new Date().toISOString();
    return {
      id: `chat-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      title: title || `New Chat`,
      messages: [createWelcomeMessage()],
      pinned: false,
      createdAt: now,
      updatedAt: now,
    };
  };

  const appendMessageToSession = (sessionId: string, message: Message) => {
    setChatSessions((prev) =>
      prev.map((session) => {
        if (session.id !== sessionId) return session;
        const nextMessages = [...session.messages, message];
        const firstUserMessage = nextMessages.find((msg) => msg.type === 'user');
        const nextTitle = firstUserMessage
          ? firstUserMessage.content.slice(0, 32)
          : session.title;
        return {
          ...session,
          title: nextTitle,
          messages: nextMessages,
          updatedAt: new Date().toISOString(),
        };
      })
    );
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Build per-user storage key (isolates chat history per user)
  useEffect(() => {
    setChatStorageKey(getChatStorageKey());
    setFabPositionStorageKey(getFabPositionStorageKey());
    setContrastStorageKey(getContrastStorageKey());
    setClarityStorageKey(getClarityStorageKey());
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(contrastStorageKey);
    setHighContrast(stored === 'true');
  }, [contrastStorageKey]);

  useEffect(() => {
    const stored = localStorage.getItem(clarityStorageKey);
    const parsed = Number(stored);
    if (Number.isFinite(parsed)) {
      setChatClarity(Math.min(100, Math.max(45, parsed)));
    }
  }, [clarityStorageKey]);

  useEffect(() => {
    localStorage.setItem(contrastStorageKey, highContrast ? 'true' : 'false');
  }, [highContrast, contrastStorageKey]);

  useEffect(() => {
    localStorage.setItem(clarityStorageKey, String(chatClarity));
  }, [chatClarity, clarityStorageKey]);

  // Sign-in greeting card anchored to COMO FAB
  useEffect(() => {
    const user = getCurrentUser();
    const userId = user.id || user.email;
    if (!userId) return;

    const sessionKey = getGreetingSeenSessionKey();
    if (sessionStorage.getItem(sessionKey)) return;

    const firstName = user.name?.split(' ')[0] || user.email?.split('@')[0] || 'there';
    const hasSeenWelcome = Boolean(localStorage.getItem(`welcome_seen_${userId}`));
    const isFirst = !hasSeenWelcome;

    setIsFirstTimeGreeting(isFirst);
    setGreetingMessage(
      isFirst
        ? `Welcome to CoreMonit, ${firstName}. Let's get your observability journey started.`
        : `Welcome back, ${firstName}. COMO is ready to help.`
    );
    setShowSignInGreeting(true);
    sessionStorage.setItem(sessionKey, 'true');

    const timeout = window.setTimeout(() => setShowSignInGreeting(false), isFirst ? 7000 : 4500);
    return () => window.clearTimeout(timeout);
  }, []);

  // Load persisted draggable position
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(fabPositionStorageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
          setFabPosition(clampPosition(parsed.x, parsed.y));
          return;
        }
      } catch {
        // Ignore malformed data and reset to default
      }
    }
    setFabPosition(getDefaultFabPosition());
  }, [fabPositionStorageKey]);

  // Persist draggable position
  useEffect(() => {
    if (!fabPosition) return;
    localStorage.setItem(fabPositionStorageKey, JSON.stringify(fabPosition));
  }, [fabPosition, fabPositionStorageKey]);

  // Keep position in viewport on resize
  useEffect(() => {
    if (!fabPosition) return;
    const onResize = () => {
      setFabPosition((prev) => (prev ? clampPosition(prev.x, prev.y) : prev));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fabPosition]);

  // Load chat sessions for this user (with migration from legacy single-thread history)
  useEffect(() => {
    const stored = localStorage.getItem(chatStorageKey);
    let loadedSessions: ChatSession[] = [];
    if (stored) {
      loadedSessions = parseStoredSessions(stored);
      if (loadedSessions.length === 0) {
        const legacyMessages = parseStoredMessages(stored);
        if (legacyMessages.length > 0) {
          const migrated = createChatSession('Main Chat');
          migrated.messages = legacyMessages;
          loadedSessions = [migrated];
        }
      }
    }
    if (loadedSessions.length === 0) {
      loadedSessions = [createChatSession('Main Chat')];
    }
    setChatSessions(loadedSessions);
    setActiveChatId(loadedSessions[0].id);
    setOpenChatIds([loadedSessions[0].id]);
    setHistoryLoaded(true);
  }, [chatStorageKey]);

  // Persist chat sessions for this user
  useEffect(() => {
    if (!historyLoaded) return;
    const serializableSessions = chatSessions.map((session) => ({
      ...session,
      messages: session.messages.map((message) => ({
        ...message,
        timestamp: message.timestamp.toISOString(),
      })),
    }));
    localStorage.setItem(chatStorageKey, JSON.stringify(serializableSessions));
  }, [chatSessions, chatStorageKey, historyLoaded]);

  // Occasional wink animation (lightweight, randomized)
  useEffect(() => {
    const interval = window.setInterval(() => {
      if (Math.random() < 0.35) {
        setIsWinking(true);
        window.setTimeout(() => setIsWinking(false), 180);
      }
    }, 6000);
    return () => window.clearInterval(interval);
  }, []);

  // Start proactive monitoring when component mounts
  useEffect(() => {
    console.log('🤖 COMO: Starting proactive monitoring...');
    
    // Add event listener for monitoring events
    const handleMonitoringEvent = (event: MonitoringEvent) => {
      console.log('🚨 COMO: Monitoring event detected:', event);
      
      // Make COMO alert to get attention
      setHasUnreadAlert(true);
      
      // Set mood based on severity
      if (event.severity === 'critical') {
        setMood('concerned');
      } else if (event.severity === 'warning') {
        setMood('alert');
      } else {
        setMood('excited');
      }

      // Add proactive message with question
      const alertMessage: Message = {
        id: event.id,
        type: 'alert',
        content: `I noticed something unusual in your infrastructure.\n\n${event.title}\n${event.message}\n\nWant me to investigate?`,
        severity: event.severity,
        suggestions: [
          'Yes, analyze this',
          'Show me details',
          'What should I do?',
          'Dismiss'
        ],
        timestamp: new Date()
      };

      setChatSessions((prev) => {
        if (prev.length === 0) {
          const seeded = createChatSession('Main Chat');
          seeded.messages = [createWelcomeMessage(), alertMessage];
          setActiveChatId(seeded.id);
          return [seeded];
        }
        const targetId = activeChatIdRef.current || prev[0].id;
        return prev.map((session) =>
          session.id === targetId
            ? {
                ...session,
                messages: [...session.messages, alertMessage],
                updatedAt: new Date().toISOString(),
              }
            : session
        );
      });

      // Reset mood after 5 seconds
      setTimeout(() => {
        if (mood !== 'idle') {
          setMood('idle');
        }
      }, 5000);
    };

    monitoringService.current.addEventListener(handleMonitoringEvent);
    monitoringService.current.startMonitoring(10000); // Check every 10 seconds

    return () => {
      monitoringService.current.removeEventListener(handleMonitoringEvent);
      monitoringService.current.stopMonitoring();
    };
  }, []);

  // Clear unread alert when opening chat
  useEffect(() => {
    if (isOpen) {
      setHasUnreadAlert(false);
    }
  }, [isOpen]);

  const handleSendMessage = async (text?: string) => {
    const messageText = text || inputValue.trim();
    if (!messageText) return;
    const targetSessionId = activeSession?.id;
    if (!targetSessionId) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    appendMessageToSession(targetSessionId, userMessage);
    setInputValue('');
    
    // Show thinking animation
    setIsTyping(true);
    setMood('thinking');

    // Send to AI with security context
    try {
      const response = await AIContextService.sendRequest(messageText);
      
      // Wait a bit to show thinking animation
      setTimeout(() => {
        const comoMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'como',
          content: response.message,
          suggestions: response.suggestions,
          severity: response.severity,
          timestamp: new Date(),
        };

        appendMessageToSession(targetSessionId, comoMessage);
        setIsTyping(false);
        
        // Set mood based on response
        if (response.severity === 'critical') {
          setMood('concerned');
        } else if (response.severity === 'warning') {
          setMood('alert');
        } else {
          setMood('happy');
        }

        // Reset to idle after a moment
        setTimeout(() => setMood('idle'), 2000);
      }, 1000);
    } catch (error) {
      console.error('Error sending AI request:', error);
      setIsTyping(false);
      setMood('concerned');
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  const handleNewChat = () => {
    const sessionCount = chatSessions.length + 1;
    const next = createChatSession(`Chat ${sessionCount}`);
    setChatSessions((prev) => [next, ...prev]);
    setActiveChatId(next.id);
    setOpenChatIds((prev) => (prev.includes(next.id) ? prev : [next.id, ...prev]));
    setInputValue('');
  };

  const handleSwitchChat = (sessionId: string) => {
    setActiveChatId(sessionId);
    setOpenChatIds((prev) => (prev.includes(sessionId) ? prev : [...prev, sessionId]));
  };

  const handleToggleOpenChat = (sessionId: string) => {
    setOpenChatIds((prev) => {
      if (prev.includes(sessionId)) {
        if (prev.length === 1) return prev;
        const next = prev.filter((id) => id !== sessionId);
        if (activeChatIdRef.current === sessionId && next.length > 0) {
          setActiveChatId(next[0]);
        }
        return next;
      }
      return [...prev, sessionId];
    });
  };

  const handleCloseOpenTab = (sessionId: string) => {
    setOpenChatIds((prev) => {
      if (prev.length === 1) return prev;
      const next = prev.filter((id) => id !== sessionId);
      if (activeChatIdRef.current === sessionId && next.length > 0) {
        setActiveChatId(next[0]);
      }
      return next;
    });
  };

  const handlePinChat = (sessionId: string) => {
    setChatSessions((prev) =>
      prev.map((session) =>
        session.id === sessionId
          ? { ...session, pinned: !session.pinned, updatedAt: new Date().toISOString() }
          : session
      )
    );
  };

  const handleStartRenameChat = (sessionId: string) => {
    const target = chatSessions.find((session) => session.id === sessionId);
    if (!target) return;
    setRenamingChatId(sessionId);
    setRenameDraft(target.title);
  };

  const handleSaveRenameChat = (sessionId: string) => {
    const nextTitle = renameDraft.trim();
    if (!nextTitle) return;
    setChatSessions((prev) =>
      prev.map((session) =>
        session.id === sessionId
          ? { ...session, title: nextTitle.slice(0, 40), updatedAt: new Date().toISOString() }
          : session
      )
    );
    setRenamingChatId(null);
    setRenameDraft('');
  };

  const handleCancelRenameChat = () => {
    setRenamingChatId(null);
    setRenameDraft('');
  };

  const handleDeleteChat = (sessionId: string) => {
    if (chatSessions.length <= 1) {
      const replacement = createChatSession('Main Chat');
      setChatSessions([replacement]);
      setActiveChatId(replacement.id);
      setOpenChatIds([replacement.id]);
      return;
    }
    setChatSessions((prev) => {
      const next = prev.filter((session) => session.id !== sessionId);
      if (activeChatIdRef.current === sessionId && next.length > 0) {
        setActiveChatId(next[0].id);
      }
      setOpenChatIds((ids) => {
        const nextIds = ids.filter((id) => id !== sessionId);
        return nextIds.length > 0 ? nextIds : [next[0].id];
      });
      return next;
    });
    setDeleteConfirmChatId(null);
  };

  const handleDeleteAction = (sessionId: string) => {
    if (deleteConfirmChatId === sessionId) {
      handleDeleteChat(sessionId);
      return;
    }
    setDeleteConfirmChatId(sessionId);
  };

  const safeAction = (
    event: { preventDefault: () => void; stopPropagation: () => void },
    action: () => void,
    keepMenuOpen = false
  ) => {
    event.preventDefault();
    event.stopPropagation();
    action();
    if (keepMenuOpen) {
      setIsChatsMenuOpen(true);
    }
  };

  const handleResetOnboarding = () => {
    const user = getCurrentUser();
    const userId = user?.id;
    if (!userId) return;

    localStorage.removeItem(`welcome_seen_${userId}`);
    localStorage.removeItem(`tour_completed_${userId}`);
    localStorage.removeItem(`tour_skipped_${userId}`);
    window.location.reload();
  };

  const handleChatHeaderPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isMobile || isTablet) return;
    const target = event.target as HTMLElement;
    if (target.closest('[data-no-chat-drag="true"]')) return;
    if (!chatPosition) return;

    setIsChatDragging(true);
    chatDragOffsetRef.current = {
      x: event.clientX - chatPosition.x,
      y: event.clientY - chatPosition.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizePointerDown = (
    event: ReactPointerEvent<HTMLDivElement | HTMLButtonElement>,
    direction: ResizeDirection
  ) => {
    if (isMobile || isTablet) return;
    if (!chatPosition) return;
    event.preventDefault();
    event.stopPropagation();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = RESIZE_CURSOR[direction];
    setIsChatResizing(true);
    chatResizeRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      width: chatSize.width,
      height: chatSize.height,
      x: chatPosition.x,
      y: chatPosition.y,
      direction,
    };
  };

  const handleFabPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const currentPosition = fabPosition || getDefaultFabPosition();
    dragOffsetRef.current = {
      x: event.clientX - currentPosition.x,
      y: event.clientY - currentPosition.y,
    };
    movedDuringDragRef.current = false;
    setIsDragging(true);
    setDragGhostPosition(currentPosition);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const updateHeaderScrollHints = () => {
    const el = headerActionsScrollRef.current;
    if (!el) return;
    const epsilon = 4;
    setCanScrollHeaderLeft(el.scrollLeft > epsilon);
    setCanScrollHeaderRight(el.scrollLeft + el.clientWidth < el.scrollWidth - epsilon);
  };

  const scrollHeaderActions = (direction: 'left' | 'right') => {
    const el = headerActionsScrollRef.current;
    if (!el) return;
    const scrollBy = Math.max(120, Math.floor(el.clientWidth * 0.65));
    el.scrollBy({
      left: direction === 'left' ? -scrollBy : scrollBy,
      behavior: 'smooth',
    });
  };

  const toggleChat = () => {
    if (movedDuringDragRef.current) {
      movedDuringDragRef.current = false;
      return;
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isDragging) return;

    const onPointerMove = (event: PointerEvent) => {
      const nextX = event.clientX - dragOffsetRef.current.x;
      const nextY = event.clientY - dragOffsetRef.current.y;
      setDragGhostPosition(clampPosition(nextX, nextY));
      movedDuringDragRef.current = true;
    };

    const onPointerUp = () => {
      if (movedDuringDragRef.current && dragGhostPosition) {
        // Cinematic spring to dropped point
        setIsDroppingFab(true);
        setFabPosition(dragGhostPosition);
        window.setTimeout(() => setIsDroppingFab(false), 1100);
      }
      setIsDragging(false);
      setDragGhostPosition(null);
      // Delay reset so click event after drag is ignored.
      setTimeout(() => {
        movedDuringDragRef.current = false;
      }, 0);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [isDragging, dragGhostPosition]);

  useEffect(() => {
    if (isMobile || isTablet || !isOpen || chatPosition || !fabPosition) return;
    const panelWidth = chatSize.width;
    const panelHeight = chatSize.height;
    const margin = 16;

    let left = fabPosition.x + 72 - panelWidth;
    if (left < margin) left = fabPosition.x;
    if (left + panelWidth > window.innerWidth - margin) {
      left = window.innerWidth - panelWidth - margin;
    }

    let top = fabPosition.y - panelHeight - 12;
    if (top < margin) top = margin;
    if (top + panelHeight > window.innerHeight - margin) {
      top = window.innerHeight - panelHeight - margin;
    }

    setChatPosition({ x: left, y: top });
  }, [isOpen, isMobile, isTablet, chatPosition, fabPosition, chatSize.width, chatSize.height]);

  useEffect(() => {
    if (!isOpen) return;
    const el = headerActionsScrollRef.current;
    if (!el) return;
    updateHeaderScrollHints();

    const onScroll = () => updateHeaderScrollHints();
    const onResize = () => updateHeaderScrollHints();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    const ResizeObserverCtor = window.ResizeObserver;
    const observer = ResizeObserverCtor ? new ResizeObserverCtor(() => updateHeaderScrollHints()) : null;
    if (observer) observer.observe(el);

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, [isOpen, isMobile, isTablet, chatSize.width, chatClarity, highContrast]);

  useEffect(() => {
    if (!isOpen) return;
    if (!activeSession?.id) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const saved = chatScrollPositionsRef.current[activeSession.id];
    window.requestAnimationFrame(() => {
      if (!messagesContainerRef.current) return;
      if (typeof saved === 'number') {
        messagesContainerRef.current.scrollTop = saved;
      } else {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    });
  }, [isOpen, activeSession?.id]);

  useEffect(() => {
    const onResize = () => {
      const nextSize = clampChatSize(chatSize);
      setChatSize(nextSize);
      setChatPosition((prev) => (prev ? clampChatPosition(prev, nextSize) : prev));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [chatSize]);

  useEffect(() => {
    if (!isChatDragging && !isChatResizing) return;

    const onPointerMove = (event: PointerEvent) => {
      if (isChatDragging) {
        const next = clampChatPosition(
          {
            x: event.clientX - chatDragOffsetRef.current.x,
            y: event.clientY - chatDragOffsetRef.current.y,
          },
          chatSize
        );
        setChatPosition(next);
      } else if (isChatResizing && chatResizeRef.current) {
        document.body.style.cursor = RESIZE_CURSOR[chatResizeRef.current.direction];
        const deltaX = event.clientX - chatResizeRef.current.startX;
        const deltaY = event.clientY - chatResizeRef.current.startY;
        const { direction } = chatResizeRef.current;
        let width = chatResizeRef.current.width;
        let height = chatResizeRef.current.height;
        let x = chatResizeRef.current.x;
        let y = chatResizeRef.current.y;

        if (direction.includes('right')) {
          width = chatResizeRef.current.width + deltaX;
        }
        if (direction.includes('left')) {
          width = chatResizeRef.current.width - deltaX;
          x = chatResizeRef.current.x + deltaX;
        }
        if (direction.includes('bottom')) {
          height = chatResizeRef.current.height + deltaY;
        }
        if (direction.includes('top')) {
          height = chatResizeRef.current.height - deltaY;
          y = chatResizeRef.current.y + deltaY;
        }

        const nextSize = clampChatSize({
          width,
          height,
        });
        if (direction.includes('left')) {
          x = chatResizeRef.current.x + (chatResizeRef.current.width - nextSize.width);
        }
        if (direction.includes('top')) {
          y = chatResizeRef.current.y + (chatResizeRef.current.height - nextSize.height);
        }
        setChatSize(nextSize);
        setChatPosition(clampChatPosition({ x, y }, nextSize));
      }
    };

    const onPointerUp = () => {
      setIsChatDragging(false);
      setIsChatResizing(false);
      chatResizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [isChatDragging, isChatResizing, chatSize]);

  const chatPanelDesktopStyle = (() => {
    if (isMobile || isTablet) return undefined;
    return {
      left: chatPosition?.x ?? 16,
      top: chatPosition?.y ?? 16,
      width: chatSize.width,
      height: chatSize.height,
    };
  })();

  const clarityRatio = chatClarity / 100;
  const panelSurfaceClass = "border rounded-2xl shadow-2xl overflow-hidden flex flex-col h-full relative";
  const bodySurfaceClass = "flex-1 min-h-0 flex";
  const messagesSurfaceClass = "flex-1 overflow-y-auto p-4 space-y-4";
  const footerSurfaceClass = "border-t p-4 flex-shrink-0";
  const panelSurfaceStyle = {
    backgroundColor: `rgba(7, 12, 22, ${Math.min(0.99, 0.76 + clarityRatio * 0.23 + (highContrast ? 0.04 : 0))})`,
    borderColor: `rgba(34, 211, 238, ${Math.min(0.7, 0.25 + clarityRatio * 0.35 + (highContrast ? 0.08 : 0))})`,
  };
  const bodySurfaceStyle = {
    backgroundColor: `rgba(3, 8, 18, ${Math.min(0.98, 0.72 + clarityRatio * 0.24 + (highContrast ? 0.03 : 0))})`,
  };
  const messagesSurfaceStyle = {
    backgroundColor: `rgba(2, 6, 14, ${Math.min(0.98, 0.68 + clarityRatio * 0.28 + (highContrast ? 0.03 : 0))})`,
  };
  const footerSurfaceStyle = {
    backgroundColor: `rgba(6, 10, 20, ${Math.min(0.98, 0.74 + clarityRatio * 0.2 + (highContrast ? 0.03 : 0))})`,
    borderColor: `rgba(34, 211, 238, ${Math.min(0.5, 0.16 + clarityRatio * 0.2 + (highContrast ? 0.08 : 0))})`,
  };

  const greetingCardStyle = useMemo(() => {
    if (!fabPosition || typeof window === 'undefined') return undefined;
    const cardWidth = isMobile ? 240 : 290;
    const maxLeft = Math.max(8, window.innerWidth - cardWidth - 8);
    const left = Math.min(Math.max(fabPosition.x - cardWidth + 44, 8), maxLeft);
    const top = Math.min(Math.max(fabPosition.y - 112, 8), Math.max(8, window.innerHeight - 128));
    return { left, top, width: cardWidth };
  }, [fabPosition, isMobile]);

  return (
    <>
      {/* Floating COMO Button */}
      <motion.button
        onClick={toggleChat}
        onPointerDown={handleFabPointerDown}
        whileHover={{ scale: isDragging ? 1 : 1.08 }}
        whileTap={{ scale: 0.95 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`fixed z-50 rounded-full shadow-2xl touch-none w-14 h-14 sm:w-16 sm:h-16 ${fabPosition ? '' : 'bottom-4 right-4 sm:bottom-6 sm:right-6'}`}
        style={
          fabPosition
            ? { left: fabPosition.x, top: fabPosition.y, cursor: isDragging ? 'grabbing' : 'grab' }
            : undefined
        }
        animate={
          fabPosition
            ? {
                left: fabPosition.x,
                top: fabPosition.y,
                rotate: isDragging ? -2 : isDroppingFab ? [0, -6, 3, 0] : 0,
                y: isDragging || isDroppingFab ? 0 : [0, -3, 0],
              }
            : undefined
        }
        transition={{
          type: 'spring',
          stiffness: isDroppingFab ? 120 : 380,
          damping: isDroppingFab ? 18 : 22,
          mass: isDroppingFab ? 1.1 : 0.7,
          y: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }
        }}
        title="COMO AI Assistant"
        data-tour="como"
      >
        <motion.div
          className="absolute inset-0 rounded-full bg-cyber-cyan/20 blur-md -z-10"
          animate={{ opacity: [0.35, 0.65, 0.35], scale: [1, 1.06, 1] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <ComoRobotIcon isWinking={isWinking} />
        {/* Drag-handle visual cue */}
        <div className="absolute -top-1 -left-1 bg-dark-900/90 border border-cyber-cyan/40 rounded-full p-0.5">
          <GripVertical className="w-3 h-3 text-cyber-cyan/80" />
        </div>
        
        {/* Unread indicator */}
        {hasUnreadAlert && !isOpen && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-dark-900 flex items-center justify-center"
          >
            <span className="text-xs font-bold text-white">!</span>
          </motion.div>
        )}
      </motion.button>

      {/* Sign-in greeting card (held beside COMO hand) */}
      <AnimatePresence>
        {showSignInGreeting && !isOpen && !isDragging && greetingCardStyle && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              boxShadow: isFirstTimeGreeting
                ? ['0 0 0 rgba(34,211,238,0)', '0 0 24px rgba(34,211,238,0.4)', '0 0 8px rgba(34,211,238,0.2)']
                : '0 0 10px rgba(34,211,238,0.2)',
            }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{
              duration: isFirstTimeGreeting ? 0.7 : 0.35,
              ease: 'easeOut',
            }}
            className={`fixed z-[58] pointer-events-none rounded-xl border px-3 py-2 ${
              isFirstTimeGreeting
                ? 'bg-gradient-to-r from-dark-900 to-dark-800 border-cyber-cyan/55'
                : 'bg-dark-900 border-cyber-cyan/35'
            }`}
            style={greetingCardStyle}
          >
            <div className="flex items-start gap-2.5">
              {isFirstTimeGreeting && <Sparkles className="w-4 h-4 text-cyber-cyan mt-0.5 flex-shrink-0" />}
              <p className="text-xs sm:text-sm text-gray-100 leading-snug">
                {greetingMessage}
              </p>
            </div>
            <div className="absolute -bottom-1.5 right-8 w-3 h-3 rotate-45 bg-dark-900 border-r border-b border-cyber-cyan/45" />
            <motion.div
              className="absolute right-0 -bottom-6 h-[2px] w-8 bg-cyber-cyan/45 origin-right"
              animate={isFirstTimeGreeting ? { scaleX: [0.7, 1, 0.85] } : undefined}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transparent draggable clone while dragging */}
      <AnimatePresence>
        {isDragging && dragGhostPosition && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 0.45, scale: 1.08 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed z-[60] w-16 h-16 rounded-full pointer-events-none"
            style={{ left: dragGhostPosition.x, top: dragGhostPosition.y }}
          >
            <div className="w-full h-full rounded-full ring-2 ring-cyber-cyan/40 border border-cyber-cyan/50 bg-cyber-cyan/10 backdrop-blur-sm flex items-center justify-center">
              <ComoRobotIcon isWinking={isWinking} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            onWheelCapture={(event) => event.stopPropagation()}
            className={
              isMobile || isTablet
                ? "fixed inset-0 z-50 flex flex-col bg-dark-900 safe-area-inset-top safe-area-inset-bottom"
                : "fixed z-50 flex flex-col"
            }
            style={chatPanelDesktopStyle}
          >
            {/* Glow effect (desktop only) */}
            {!isMobile && !isTablet && (
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-cyan/20 to-cyber-blue/20 blur-2xl -z-10" />
            )}

            {/* Panel */}
            <div className={
              isMobile || isTablet
                ? "flex-1 flex flex-col h-full"
                : panelSurfaceClass
            } style={isMobile || isTablet ? undefined : panelSurfaceStyle}>
              {/* Header */}
              <div
                onPointerDown={handleChatHeaderPointerDown}
                className={`bg-gradient-to-r from-cyber-cyan/12 to-cyber-blue/12 border-b border-cyber-cyan/20 p-4 flex items-center justify-between flex-shrink-0 ${
                  isMobile || isTablet ? '' : 'cursor-move'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-10 h-10 shrink-0">
                    <ComoRobotIcon mood={mood} isHovered={isHovered} isWinking={isWinking} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-white text-base leading-tight whitespace-nowrap">COMO AI</h3>
                    <p className="text-xs text-gray-300 truncate max-[430px]:hidden">
                      {mood === 'thinking' ? 'Analyzing...' : 
                       mood === 'alert' ? 'Alert detected' :
                       mood === 'concerned' ? 'Issue detected' :
                       mood === 'excited' ? 'New insights!' :
                       'Always monitoring'}
                    </p>
                  </div>
                </div>
                
                <div className="relative min-w-0 max-w-[calc(100%-10rem)]" data-no-chat-drag="true">
                  <div
                    ref={headerActionsScrollRef}
                    className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap pl-9 pr-9 scrollbar-thin scrollbar-thumb-cyber-cyan/30 scrollbar-track-transparent"
                  >
                    <DropdownMenu open={isChatsMenuOpen} onOpenChange={setIsChatsMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <button
                        className="shrink-0 px-2.5 py-2 hover:bg-dark-800 rounded-lg transition-colors text-xs text-gray-200 border border-cyber-cyan/20 inline-flex items-center gap-1.5"
                          title="Chats"
                          aria-label="Chats"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span>Chats</span>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72 p-2">
                      <div className="space-y-1">
                        {sortedSessions.map((session) => (
                          <div key={session.id} className="rounded-md border border-dark-700 bg-dark-900 p-2">
                            {renamingChatId === session.id ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  value={renameDraft}
                                  onChange={(e) => setRenameDraft(e.target.value)}
                                  className="flex-1 h-7 rounded-md bg-dark-800 border border-dark-700 px-2 text-xs text-white"
                                />
                                <button
                                  onPointerDown={(e) => safeAction(e, () => handleSaveRenameChat(session.id), true)}
                                  className="p-1 rounded hover:bg-dark-700"
                                  title="Save name"
                                >
                                  <Check className="w-3.5 h-3.5 text-cyber-cyan" />
                                </button>
                                <button
                                  onPointerDown={(e) => safeAction(e, handleCancelRenameChat, true)}
                                  className="p-1 rounded hover:bg-dark-700"
                                  title="Cancel rename"
                                >
                                  <X className="w-3.5 h-3.5 text-gray-200" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onPointerDown={(e) => safeAction(e, () => handleSwitchChat(session.id), false)}
                                  className="w-full text-left"
                                >
                                  <div className="flex items-center gap-1.5">
                                    <MessageSquare className="w-3.5 h-3.5 text-gray-300" />
                                    {session.pinned && <Pin className="w-3.5 h-3.5 text-cyber-cyan" />}
                                    <span className="truncate text-xs text-gray-200">{session.title}</span>
                                  </div>
                                </button>
                                <div className="mt-1.5 flex items-center justify-end gap-1">
                                  <button
                                    onPointerDown={(e) => safeAction(e, () => handleToggleOpenChat(session.id), true)}
                                    className="p-1 rounded hover:bg-dark-700/70"
                                    title={openChatIds.includes(session.id) ? 'Close from open tabs' : 'Open in parallel'}
                                  >
                                    {openChatIds.includes(session.id) ? (
                                      <Check className="w-3.5 h-3.5 text-cyber-cyan" />
                                    ) : (
                                      <Plus className="w-3.5 h-3.5 text-gray-200" />
                                    )}
                                  </button>
                                  <button
                                    onPointerDown={(e) => safeAction(e, () => handlePinChat(session.id), true)}
                                    className="p-1 rounded hover:bg-dark-700/70"
                                    title={session.pinned ? 'Unpin chat' : 'Pin chat'}
                                  >
                                    <Pin className={`w-3.5 h-3.5 ${session.pinned ? 'text-cyber-cyan' : 'text-gray-200'}`} />
                                  </button>
                                  <button
                                    onPointerDown={(e) => safeAction(e, () => handleStartRenameChat(session.id), true)}
                                    className="p-1 rounded hover:bg-dark-700/70"
                                    title="Rename chat"
                                  >
                                    <Pencil className="w-3.5 h-3.5 text-gray-200" />
                                  </button>
                                  <button
                                    onPointerDown={(e) => safeAction(e, () => handleDeleteAction(session.id), true)}
                                    className="p-1 rounded hover:bg-dark-700/70"
                                    title={deleteConfirmChatId === session.id ? 'Click again to confirm delete' : 'Delete chat'}
                                  >
                                    <Trash2 className={`w-3.5 h-3.5 ${deleteConfirmChatId === session.id ? 'text-red-400' : 'text-gray-200'}`} />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </DropdownMenuContent>
                    </DropdownMenu>
                    <button
                      onClick={handleNewChat}
                      className="shrink-0 p-2 hover:bg-dark-800 rounded-lg transition-colors"
                      title="New Chat"
                      aria-label="New Chat"
                    >
                      <Plus className="w-4 h-4 text-gray-300" />
                    </button>
                    <button
                      onClick={handleResetOnboarding}
                      className="shrink-0 p-2 hover:bg-dark-800 rounded-lg transition-colors"
                      title="Reset Onboarding Tour"
                      aria-label="Reset Onboarding Tour"
                    >
                      <RotateCcw className="w-4 h-4 text-gray-200" />
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="shrink-0 p-2 hover:bg-dark-800 rounded-lg transition-colors"
                      title="Close COMO chat"
                      aria-label="Close COMO chat"
                    >
                      <X className="w-5 h-5 text-gray-200" />
                    </button>
                  </div>
                  <AnimatePresence>
                    {canScrollHeaderLeft && (
                      <motion.button
                        key="header-scroll-left"
                        type="button"
                        onClick={() => scrollHeaderActions('left')}
                        data-no-chat-drag="true"
                        initial={{ opacity: 0, x: 4 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 4 }}
                        className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-dark-900/90 border border-cyber-cyan/35 text-cyber-cyan flex items-center justify-center"
                        title="Scroll left for more options"
                        aria-label="Scroll left for more options"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </motion.button>
                    )}
                  </AnimatePresence>
                  <AnimatePresence>
                    {canScrollHeaderRight && (
                      <motion.button
                        key="header-scroll-right"
                        type="button"
                        onClick={() => scrollHeaderActions('right')}
                        data-no-chat-drag="true"
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -4 }}
                        className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-dark-900/90 border border-cyber-cyan/35 text-cyber-cyan flex items-center justify-center"
                        title="More options to the right. Scroll to view."
                        aria-label="More options to the right. Scroll to view."
                      >
                        <motion.span
                          animate={{ x: [0, 2, 0] }}
                          transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                          className="inline-flex"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </motion.span>
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className={bodySurfaceClass} style={bodySurfaceStyle}>
                {/* Messages */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="px-3 pt-2 pb-1 border-b border-cyber-cyan/10 overflow-x-auto">
                    <div className="flex gap-2 min-w-max">
                      {openChatIds
                        .map((id) => chatSessions.find((s) => s.id === id))
                        .filter((session): session is ChatSession => Boolean(session))
                        .map((session) => (
                          <div
                            key={session.id}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs ${
                              session.id === activeSession?.id
                                ? 'bg-cyber-cyan/18 text-cyber-cyan border border-cyber-cyan/30'
                                : 'bg-dark-800/70 text-gray-300'
                            }`}
                          >
                            <button onClick={() => handleSwitchChat(session.id)} className="inline-flex items-center gap-1">
                              {session.pinned && <Pin className="w-3 h-3" />}
                              <span>{session.title}</span>
                            </button>
                            <button
                              onClick={() => handleCloseOpenTab(session.id)}
                              className="ml-1 rounded hover:bg-dark-700/70 p-0.5"
                              title="Close tab"
                              aria-label="Close tab"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div
                    ref={messagesContainerRef}
                    onScroll={() => {
                      if (!activeSession?.id || !messagesContainerRef.current) return;
                      chatScrollPositionsRef.current[activeSession.id] = messagesContainerRef.current.scrollTop;
                    }}
                    className={`${messagesSurfaceClass} overscroll-contain`}
                    style={messagesSurfaceStyle}
                  >
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            message.type === 'user'
                              ? 'bg-gradient-to-r from-cyber-cyan to-cyber-blue text-white shadow-lg'
                              : message.type === 'alert'
                              ? `${message.severity === 'critical' ? 'bg-red-900' : message.severity === 'warning' ? 'bg-orange-900' : 'bg-blue-900'} border ${message.severity === 'critical' ? 'border-red-500/50' : message.severity === 'warning' ? 'border-orange-500/50' : 'border-blue-500/50'} text-white shadow-lg backdrop-blur-sm`
                              : 'bg-cyber-cyan/12 border border-cyber-cyan/30 text-cyan-50 shadow-lg backdrop-blur-sm'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap font-medium">{message.content}</p>
                          
                          {/* Suggestions */}
                          {message.suggestions && message.suggestions.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {message.suggestions.map((suggestion, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => handleSuggestionClick(suggestion)}
                                  className="w-full text-left px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-xs text-gray-300 transition-colors touch-target"
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-300 mt-1">
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* Typing indicator */}
                    {isTyping && (
                      <div className="flex justify-start">
                        <div className="bg-dark-800 rounded-lg p-3">
                          <div className="flex gap-1">
                            {[0, 1, 2].map(i => (
                              <motion.div
                                key={i}
                                animate={{ y: [0, -8, 0] }}
                                transition={{
                                  duration: 0.6,
                                  repeat: Infinity,
                                  delay: i * 0.1
                                }}
                                className="w-2 h-2 rounded-full bg-cyber-cyan"
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                </div>
              </div>

              {/* Input */}
              <div className={footerSurfaceClass} style={footerSurfaceStyle}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Ask COMO anything..."
                    className="flex-1 bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white placeholder-gray-300 focus:outline-none focus:border-cyber-cyan/50 transition-colors min-h-[44px]"
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-cyber-cyan to-cyber-blue text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity touch-target"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                
                <p className="text-xs text-gray-300 mt-2">
                  🔒 Secured with tenant isolation • All data stays private
                </p>
              </div>

              {!isMobile && !isTablet && (
                <>
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'top')} className="absolute top-0 left-3 right-3 h-1 cursor-ns-resize" />
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'bottom')} className="absolute bottom-0 left-3 right-3 h-1 cursor-ns-resize" />
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'left')} className="absolute left-0 top-3 bottom-3 w-1 cursor-ew-resize" />
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'right')} className="absolute right-0 top-3 bottom-3 w-1 cursor-ew-resize" />
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'top-left')} className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize" />
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'top-right')} className="absolute top-0 right-0 w-3 h-3 cursor-nesw-resize" />
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'bottom-left')} className="absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize" />
                  <div data-no-chat-drag="true" onPointerDown={(e) => handleResizePointerDown(e, 'bottom-right')} className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize" />
                  <button
                    data-no-chat-drag="true"
                    onPointerDown={(e) => handleResizePointerDown(e, 'bottom-right')}
                    className="absolute bottom-1.5 right-1.5 p-1.5 rounded-md text-gray-200 hover:text-cyber-cyan hover:bg-dark-800/80 transition-colors cursor-nwse-resize"
                    title="Resize chat window"
                    aria-label="Resize chat window"
                  >
                    <Move className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}